import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import c from 'classnames';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData, DefaultBiEvent } from '@wix/da-bi/pkg/events';
import TagList from '@wix/da-ds/pkg/TagList';
import { TagType } from '@wix/da-ds/pkg/TagList/types';
import ArrowDown from '@wix/da-ds/pkg/Icons/ArrowDown';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import s from './TagSelection.scss';

export interface Props {
  tags: string[];
  selectedTags: string[];
  onTagSelectionChange: (tags: string[]) => void;
  className?: string;
}

const TagSelection: React.FC<Props> = ({
  tags,
  selectedTags,
  onTagSelectionChange,
  className,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const logEvent = useBiLogger();

  const allAvailableTags = useMemo(() => {
    return tags.slice(0, 30);
  }, [tags]);

  const handleAddTag = useCallback(
    (tagName: string) => {
      if (selectedTags.length < 3) {
        onTagSelectionChange([...selectedTags, tagName]);
      }
    },
    [selectedTags, onTagSelectionChange]
  );

  const handleRemoveTag = useCallback(
    (tagName: string) => {
      onTagSelectionChange(selectedTags.filter(tag => tag !== tagName));
    },
    [selectedTags, onTagSelectionChange]
  );

  const tagItems: TagType[] = useMemo(
    () =>
      allAvailableTags.map(tagName => {
        const isSelected = selectedTags.includes(tagName);
        const isAtLimit = selectedTags.length >= 3;

        return {
          tagname: tagName,
          isSelected,
          isRemovable: false, // Selected tags get X button
          isRecommended: true, // Unselected tags get + button
          disabled: !isSelected && isAtLimit, // Disable unselected when at limit
          className: s['tag'],
          onClick: isSelected
            ? () => handleRemoveTag(tagName)
            : () => handleAddTag(tagName),
        };
      }),
    [allAvailableTags, selectedTags, handleAddTag, handleRemoveTag]
  );

  const onToggle = useCallback(() => {
    setIsExpanded(!isExpanded);
    logEvent(BiData<DefaultBiEvent>({
      evid: 2,
      click_info: isExpanded ? 'close_tag_settings' : 'open_tag_settings',
    }));
  }, [isExpanded, setIsExpanded, logEvent]);

  const hasTagsAvailable = allAvailableTags.length > 0;

  return (
    <div className={c(s['tag-selection'], className)}>
      <button
        type="button"
        className={c(s['toggle-button'], isExpanded && s['expanded'])}
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <span className={s['toggle-text']}>
          {t('boostDeviation.modal.advancedTagSettings')}
        </span>
        <ArrowDown className={s['chevron']} size={IconSize.SMALL} />
      </button>

      {isExpanded && (
        <div className={s['tag-content']}>
          {!hasTagsAvailable ? (
            <div className={s['no-tags']}>
              {t('boostDeviation.modal.noTagsAvailable')}
            </div>
          ) : (
            <>
              <div className={s['tag-instruction']}>
                {t('boostDeviation.modal.selectUpToThreeTags')}
              </div>
              <TagList
                tags={tagItems}
                size="small"
                multiline
                className={s['tag-list']}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TagSelection;
